import { ReactNode, FC } from 'react';
import { css } from '@emotion/core';
import { useQuery } from '@apollo/client';
import { Flex } from '@rebass/emotion';
import get from 'lodash.get';

import StepDot from '../StepDot';
import Subheader from '../Subheader';
import { CartTypeQuery } from '../../graphql/cart';

import CHECKOUT_STEPS_ROUTES from '../../routes/checkout';

import { DOWNLOAD_ONLY } from '../../constants/cartTypes';
import messages from './Steps.messages';
import { rem } from '../../styles/theme/layout';
import RouteProps from '../RoutesWithLayout/RouteProps';
import StepLine from '../StepLine';
import {
  CartTypeQuery as CartTypeQueryResults,
  CartTypeQueryVariables,
  CartTypeQuery_user_cart,
} from '../../graphql/__generated__/CartTypeQuery';

export interface Step {
  title: object;
  screen: string;
  route: string;
}

export interface StepState {
  isActive: boolean;
  isPassed: boolean;
  isLast: boolean;
}

const flexContainer = css`
  align-content: center;
  height: 100%;
  justify-content: space-between;
  align-items: center;
  padding: ${rem(14)} 0 ${rem(34)};
`;

export const ALL_STEPS: { [label: string]: Step } = {
  address: {
    title: messages.address,
    screen: 'ADDRESS',
    route: CHECKOUT_STEPS_ROUTES.address.path,
  },
  delivery: {
    title: messages.shipping,
    screen: 'DELIVERY',
    route: CHECKOUT_STEPS_ROUTES.delivery.path,
  },
  review: {
    title: messages.review,
    screen: 'REVIEW_ORDER',
    route: CHECKOUT_STEPS_ROUTES.review.path,
  },
  payment: {
    title: messages.payment,
    screen: 'PAYMENT',
    route: CHECKOUT_STEPS_ROUTES.payment.path,
  },
};

const getActiveSteps = (cart: CartTypeQuery_user_cart): Step[] => {
  if (cart.type === DOWNLOAD_ONLY) {
    return [ALL_STEPS.address, ALL_STEPS.review, ALL_STEPS.payment];
  }
  return [
    ALL_STEPS.address,
    ALL_STEPS.delivery,
    ALL_STEPS.review,
    ALL_STEPS.payment,
  ];
};

const getStepIndexForRoute = (steps: Step[], route: string): number =>
  steps.findIndex((step) => step.route === route);

const renderDotWithLine = (
  step: Step,
  number: number,
  stepState: StepState
): ReactNode => {
  return [
    <StepDot
      step={step}
      number={number}
      key={`step-${step.route}`}
      stepState={stepState}
    />,
    !stepState.isLast && (
      <StepLine
        key={`line-${step.route}`}
        stepState={stepState}
        data-testid={`checkout-step-line-${number}`}
      />
    ),
  ];
};

const Steps: FC<RouteProps> = ({ match }) => {
  const { data, loading, error } = useQuery<
    CartTypeQueryResults,
    CartTypeQueryVariables
  >(CartTypeQuery);

  if (loading || error || !data) {
    return null;
  }

  const cart = get(data, 'user.cart');

  if (!cart) return null;

  const steps = getActiveSteps(cart);

  return (
    <div className="bb b--black-10 bw1">
      <Subheader backgroundColor="near-white">
        <div className="ph3 ph0-l measure-wide h-100 v-mid center">
          <Flex css={flexContainer}>
            {steps.map((step, index) => {
              const stepState: StepState = {
                isActive: step.route === match.path,
                isPassed: getStepIndexForRoute(steps, match.path) > index,
                isLast: steps.length - 1 === index,
              };

              return renderDotWithLine(step, index + 1, stepState);
            })}
          </Flex>
        </div>
      </Subheader>
    </div>
  );
};

export default Steps;
